import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, Users, Activity, PieChart as PieIcon } from 'lucide-react';
import {
  groupByStatus, groupByDate, groupByStaff, groupByService, computeOpsMetrics,
  STATUS_HEX, statusLabel,
} from './calendarAnalyticsAggregate';

/**
 * Day 11 — Calendar Operational Charts (frontend-only).
 *
 * Derives from `filteredShifts` already present in the calendar page.
 * No API calls, no mutation. Charts match the currently visible dataset.
 * Group-session policy documented in calendarAnalyticsAggregate.js.
 */

const SERVICE_PALETTE = ['#0f766e', '#0369a1', '#b45309', '#9f1239', '#047857', '#1e40af', '#b91c1c', '#065f46', '#334155', '#7e22ce'];

function Metric({ label, value, hint, testId }) {
  return (
    <div className="rounded-lg border bg-card p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold leading-tight">{value}</div>
      {hint ? <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div> : null}
    </div>
  );
}

function ChartCard({ title, icon: Icon, empty, testId, children, footer }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-1">
        <CardTitle className="text-xs flex items-center gap-1.5 text-muted-foreground font-medium">
          {Icon ? <Icon className="w-3.5 h-3.5" /> : null}{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        {empty ? (
          <div className="h-[180px] flex items-center justify-center text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
            No data available
          </div>
        ) : (
          <div className="h-[180px] w-full min-w-0">{children}</div>
        )}
        {footer ? <div className="text-[10px] text-muted-foreground mt-1">{footer}</div> : null}
      </CardContent>
    </Card>
  );
}

export function CalendarOperationalCharts({ shifts }) {
  const list = Array.isArray(shifts) ? shifts : [];

  const byStatus = useMemo(() => { try { return groupByStatus(list); } catch { return []; } }, [list]);
  const byDate = useMemo(() => { try { return groupByDate(list); } catch { return []; } }, [list]);
  const byStaff = useMemo(() => { try { return groupByStaff(list); } catch { return []; } }, [list]);
  const byService = useMemo(() => { try { return groupByService(list); } catch { return []; } }, [list]);
  const metrics = useMemo(() => { try { return computeOpsMetrics(list); } catch { return null; } }, [list]);

  // Overall empty state (zero shifts) — compact single message instead of 4 empty cards.
  if (list.length === 0) {
    return (
      <div data-testid="calendar-analytics-empty" className="text-xs text-muted-foreground px-3 py-4 border rounded-lg bg-muted/20 text-center">
        No shift data for the current filters — operational charts will appear here.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="calendar-operational-charts">
      {/* Metric strip — quick ops context */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric testId="ops-metric-total" label="Shifts in view" value={metrics?.total ?? 0} />
        <Metric testId="ops-metric-coverage-risk"
          label="Coverage at risk"
          value={metrics?.coverageRisk ?? 0}
          hint={metrics?.total ? `${metrics.coverageRiskPct}% OPEN or PARTIAL` : null}
        />
        <Metric testId="ops-metric-days" label="Days covered" value={metrics?.uniqueDays ?? 0} />
        <Metric testId="ops-metric-staff" label="Staff on roster" value={metrics?.uniqueStaff ?? 0} />
      </div>

      {/* 4 charts row — compact, responsive */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <ChartCard title="Coverage breakdown" icon={PieIcon} testId="ops-chart-coverage" empty={byStatus.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v, _n, p) => [v, statusLabel(p.payload.status)]}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: 10 }}
                formatter={(v) => statusLabel(v)}
              />
              <Pie
                data={byStatus}
                dataKey="count"
                nameKey="status"
                innerRadius={34}
                outerRadius={58}
                paddingAngle={1}
              >
                {byStatus.map((e, i) => (
                  <Cell key={i} fill={STATUS_HEX[e.status] || STATUS_HEX.UNKNOWN} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Daily load"
          icon={Activity}
          testId="ops-chart-daily"
          empty={byDate.length === 0}
          footer={byDate.length ? `${byDate.length} day${byDate.length === 1 ? '' : 's'} in range` : null}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byDate} margin={{ top: 6, right: 8, bottom: 0, left: -18 }} barCategoryGap="16%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} width={28} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v) => [v, 'Shifts']}
              />
              <Bar dataKey="count" fill="#0369a1" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Staff workload (top 10)" icon={Users} testId="ops-chart-staff" empty={byStaff.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={byStaff}
              layout="vertical"
              margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
              barCategoryGap="22%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v) => [v, 'Shifts']}
              />
              <Bar dataKey="count" fill="#0f766e" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Service distribution" icon={BarChart3} testId="ops-chart-service" empty={byService.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Tooltip
                contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                formatter={(v, n) => [v, n]}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: 10 }}
              />
              <Pie
                data={byService}
                dataKey="value"
                nameKey="name"
                innerRadius={34}
                outerRadius={58}
                paddingAngle={1}
              >
                {byService.map((_, i) => <Cell key={i} fill={SERVICE_PALETTE[i % SERVICE_PALETTE.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

export default CalendarOperationalCharts;
